import { Platform } from 'react-native';
import { saveLog, store } from '@reduxLocal/persistState';
import {
	addAbortController,
	removeAbortController,
} from '@utils/abortController';
import { addToQueue } from './requestQueue';
import {
	SHOW_LOADING_MODAL,
	HIDE_LOADING_MODAL,
	HIDE_ALL_MODALS,
	SHOW_AUTH_MODAL,
} from '@reduxLocal/actions/types';
import { getNetworkStatus } from './networkManager';
import { dispatchOfflineAuthModal } from './offlineDialogThrottle';
import {
	isSensitiveOnlineRequiredRoute,
	shouldQueueOfflineMutation,
	isScopedOptimisticRoute,
	isMutationMethod,
} from './offlinePolicy';
import { isOfflineModeEntitled } from './offlineEntitlement';
import { getSessionScopeIdFromStore } from './sessionScope';
import { attachDemoSandboxAdapter } from './demoCompanySandbox';

function dispatch(action: any) {
	store.dispatch(action);
}

// Optimized interceptor utilities to reduce code duplication and improve performance
class OptimizedAPIInterceptor {
	private requestCache = new Map<
		string,
		{ timestamp: number; config: any }
	>();
	private readonly cacheTTL = 50; // 0.05 seconds cache for duplicate request prevention

	// Request interceptor factory
	createRequestInterceptor(
		options: {
			shouldLog?: boolean;
			shouldQueue?: boolean;
			shouldShowModal?: boolean;
			monitorType?: string;
		} = {}
	) {
		const {
			shouldLog = true,
			shouldQueue = true,
			shouldShowModal = false,
			monitorType = 'default',
		} = options;

		return async (config: any) => {
			// Create unique request key including params for GET requests
			const paramsKey = config.params
				? `-${JSON.stringify(config.params)}`
				: '';
			const requestKey = `${config.method}-${config.url}${paramsKey}`;
			const startTime = Date.now();

			// Deduplicate only mutating requests (double-submit guard).
			// Read/idempotent calls (GET, and POST used as fetches) must never be
			// rejected — React Query remounts, Strict Mode, and parallel screens
			// routinely fire the same read within the TTL window.
			const dedupe = this.shouldDeduplicate(config);
			if (dedupe && this.isDuplicateRequest(requestKey, config)) {
				return Promise.reject({
					message: 'Duplicate request prevented',
					isDuplicate: true,
					config,
				});
			}

			// Add abort controller for request management
			addAbortController(requestKey, config);

			// Cache only mutations — reads skip the map so they never collide
			if (dedupe) {
				this.requestCache.set(requestKey, { timestamp: startTime, config });
			}

			// Optimized logging - only log if enabled and on web
			if (shouldLog && Platform.OS === 'web') {
				const log = {
					type: 'API_REQUEST',
					method: config.method?.toLowerCase(),
					url: this.sanitizeUrl(config.url),
					monitorType,
					timestamp: new Date().toISOString(),
					requestId: requestKey,
				};

				// Use optimized logging (non-blocking)
				saveLog(log).catch((err) =>
					console.warn('Logging failed:', err)
				);
			}

			await attachDemoSandboxAdapter(config);
			if (config?._demoSandboxLocal) {
				config._monitorType = monitorType;
				config._shouldShowModal = shouldShowModal;
				return config;
			}

			// Handle offline queueing with shared web/native network state.
			const isOffline = !getNetworkStatus();

			if (isOffline) {
				const routeRequiresOnline = isSensitiveOnlineRequiredRoute(
					config.url
				);
				const shouldQueueThisRequest =
					shouldQueue &&
					shouldQueueOfflineMutation(config.url, config.method);

				if (routeRequiresOnline) {
					dispatchOfflineAuthModal(
						'This action requires an internet connection. Please reconnect and try again.'
					);

					return Promise.reject({
						message:
							'Action blocked. This route requires an online connection.',
						requiresOnline: true,
					});
				}

				const isScopedMutation =
					isScopedOptimisticRoute(config.url) &&
					isMutationMethod(config.method);

				if (isScopedMutation && !isOfflineModeEntitled()) {
					dispatchOfflineAuthModal(
						'Offline mode requires an active subscription. Please reconnect or upgrade your plan.'
					);
					return Promise.reject({
						message:
							'Offline mode requires an active subscription.',
						requiresSubscription: true,
						requiresOnline: true,
					});
				}

				if (shouldQueueThisRequest) {
					const idempotencyKey =
						config?.headers?.['x-idempotency-key'] ||
						`${config.method}-${config.url}-${Date.now()}`;
					await addToQueue({
						...config,
						sessionScopeId: getSessionScopeIdFromStore(),
						offlineScopedRoute: true,
						queuedAt: Date.now(),
						idempotencyKey,
						retryCount: 0,
						headers: {
							...(config.headers || {}),
							'x-idempotency-key': idempotencyKey,
						},
					});
					// Queue silently after first notice — resume/NetInfo flaps used to re-fire AuthModal per request.
					dispatchOfflineAuthModal(
						'You are offline. This update will sync automatically when connection returns.'
					);
					return Promise.reject({
						message: 'Request queued. Offline mode.',
						isQueued: true,
					});
				}
			}

			// Tag config with monitoring metadata for response interceptor
			config._monitorType = monitorType;
			config._shouldShowModal = shouldShowModal;

			// Show loading modal if requested
			if (shouldShowModal) {
				dispatch({ type: SHOW_LOADING_MODAL });
			}

			return config;
		};
	}

	// Response interceptor factory
	createResponseInterceptor(
		options: {
			shouldLog?: boolean;
			shouldHideModal?: boolean;
			monitorType?: string;
		} = {}
	) {
		const {
			shouldLog = true,
			shouldHideModal = false,
			monitorType = 'default',
		} = options;

		return (response: any) => {
			const paramsKey = response.config.params
				? `-${JSON.stringify(response.config.params)}`
				: '';
			const requestKey = `${response.config.method}-${response.config.url}${paramsKey}`;
			const cachedRequest = this.requestCache.get(requestKey);

			// Clean up abort controller
			removeAbortController(requestKey);

			// Clean up cache
			this.requestCache.delete(requestKey);

			// Log successful response
			if (shouldLog && Platform.OS === 'web' && cachedRequest) {
				const log = {
					type: 'API_RESPONSE',
					method: response.config.method?.toLowerCase(),
					url: this.sanitizeUrl(response.config.url),
					status: response.status,
					duration: Date.now() - cachedRequest.timestamp,
					monitorType,
					timestamp: new Date().toISOString(),
					requestId: requestKey,
					responseSize: this.getResponseSize(response),
				};

				saveLog(log).catch((err) =>
					console.warn('Response logging failed:', err)
				);

				// Track AI requests in context tracker (async, non-blocking)
				const url = response.config.url || '';
				if (url.includes('/ai/help') || url.includes('/ai/gen') || url.includes('/ai/chat')) {
					import('@services/contextTracker').then(({ addContextEntry }) => {
						try {
							const requestContent = JSON.stringify({
								url: url,
								method: response.config.method,
								requestData: response.config.data ? (typeof response.config.data === 'string' ? JSON.parse(response.config.data) : response.config.data) : null
							});
							const responseContent = JSON.stringify({
								response: response.data?.response || response.data?.message || response.data,
								status: response.status
							});
							const fullContent = `Request: ${requestContent}\nResponse: ${responseContent}`;
							addContextEntry('ai_request', fullContent, {
								url,
								method: response.config.method,
								status: response.status,
								timestamp: new Date().toISOString()
							}).catch((err) => {
								if (__DEV__) {
									console.warn('Failed to track AI request in context:', err);
								}
							});
						} catch (error) {
							// Silently fail - context tracking is optional
							if (__DEV__) {
								console.warn('Failed to track AI request in context:', error);
							}
						}
					}).catch(() => {
						// Module import failed, ignore
					});
				}
			}

			// Handle billing warnings
			if (response.data?.billingWarning) {
				const routeName = response.config.url.split('/')[1];
				// Handle billing warning (import paymentDispatcher if needed)
				console.warn('Billing warning for route:', routeName);
			}

			// Show success modal for monitored requests
			if (response.config._shouldShowSuccessModal) {
				// Show success message from response or default
				const successMessage =
					response.data?.message ||
					'Operation completed successfully';

				dispatch({
					type: SHOW_AUTH_MODAL,
					data: {
						content: successMessage,
						type: 'success',
					},
				});
			} else if (shouldHideModal) {
				dispatch({ type: HIDE_LOADING_MODAL });
			}

			return response;
		};
	}

	// Error interceptor factory
	createErrorInterceptor(
		options: {
			shouldLog?: boolean;
			shouldShowErrorModal?: boolean;
			monitorType?: string;
		} = {}
	) {
		const {
			shouldLog = true,
			shouldShowErrorModal = true,
			monitorType = 'default',
		} = options;

		return (error: any) => {
			try {
				const { maybeHandleSessionExpiredFromAxiosError } = require('@utils/handleSessionExpired');
				if (maybeHandleSessionExpiredFromAxiosError(error)) {
					return Promise.reject(error);
				}
			} catch {
				/* session handler optional at bootstrap */
			}

			const paramsKey = error.config?.params
				? `-${JSON.stringify(error.config.params)}`
				: '';
			const requestKey = `${error.config?.method}-${error.config?.url}${paramsKey}`;
			const cachedRequest = this.requestCache.get(requestKey);

			// Clean up
			removeAbortController(requestKey);
			this.requestCache.delete(requestKey);

			// Skip logging for duplicate/queued requests
			if (error.isDuplicate || error.isQueued) {
				return Promise.reject(error);
			}

			// Log error
			if (shouldLog && Platform.OS === 'web' && cachedRequest) {
				const log = {
					type: 'API_ERROR',
					method: error.config?.method?.toLowerCase(),
					url: this.sanitizeUrl(error.config?.url),
					status: error.response?.status,
					error: error.message,
					duration: Date.now() - cachedRequest.timestamp,
					monitorType,
					timestamp: new Date().toISOString(),
					requestId: requestKey,
				};

				saveLog(log).catch((err) =>
					console.warn('Error logging failed:', err)
				);
			}

			// Handle payment errors
			if (
				error.response?.status === 402 ||
				error.response?.status === 403
			) {
				console.warn('Payment required for:', error.config?.url);
				// paymentDispatcher(error.config?.url, 'error');
			}

			// Show error modal for monitored requests
			if (error.config?._shouldShowModal && shouldShowErrorModal) {
				const errorMessage =
					error.response?.data?.message ||
					error.response?.data?.error ||
					error.message ||
					'An error occurred';

				dispatch({
					type: SHOW_AUTH_MODAL,
					data: {
						content: errorMessage,
						type: 'error',
					},
				});
			} else {
				console.log('🚨 apiInterceptor: NOT showing error modal', {
					hasConfig: !!error.config,
					shouldShowModal: error.config?._shouldShowModal,
					shouldShowErrorModal,
					monitorType
				});
			}

			return Promise.reject(error);
		};
	}

	/**
	 * Only guard against accidental double-submits on mutations.
	 * This codebase often uses POST for reads (`/get-*`, `/list-*`, etc.).
	 */
	private shouldDeduplicate(config: any): boolean {
		if (config?.skipDuplicateCheck) return false;

		const method = String(config?.method || 'get').toLowerCase();
		if (method === 'get' || method === 'head' || method === 'options') {
			return false;
		}

		const url = String(config?.url || '');
		// POST/PUT used as fetches — never treat as double-submit
		if (/\/(get-|list-|fetch-|search|query|load-|retrieve)/i.test(url)) {
			return false;
		}

		return method === 'post' || method === 'put' || method === 'patch' || method === 'delete';
	}

	private isDuplicateRequest(requestKey: string, config: any): boolean {
		const cached = this.requestCache.get(requestKey);
		if (!cached) return false;

		const now = Date.now();
		const isWithinTTL = now - cached.timestamp < this.cacheTTL;

		// Check both data (POST/PUT) and params (GET) for duplicate detection
		const isSamePayload =
			JSON.stringify(config.data) ===
				JSON.stringify(cached.config.data) &&
			JSON.stringify(config.params) ===
				JSON.stringify(cached.config.params);

		return isWithinTTL && isSamePayload;
	}

	private sanitizeUrl(url: string): string {
		if (!url) return 'unknown';
		// Remove sensitive query parameters
		return url.split('?')[0].replace(/\/\d+/g, '/:id');
	}

	private getResponseSize(response: any): number {
		try {
			return JSON.stringify(response.data).length;
		} catch {
			return 0;
		}
	}

	// Clean up old cache entries periodically
	cleanupCache(): void {
		const now = Date.now();
		for (const [key, value] of this.requestCache.entries()) {
			if (now - value.timestamp > this.cacheTTL * 2) {
				this.requestCache.delete(key);
			}
		}
	}
}

// Create singleton instance
export const apiInterceptor = new OptimizedAPIInterceptor();

// Clean up cache every 30 seconds
setInterval(() => {
  apiInterceptor.cleanupCache();
}, 30000);

export default apiInterceptor;
